/**
 * MentionNodeTree - renders a Taiga node tree under a Slack mention.
 * Uses the shared NodeRow component (same look & interaction as TaigaSheetDetailPage).
 * Manages its own local state for focus, editing, drag-and-drop, keyboard shortcuts, etc.
 */
import type { FC } from "react"
import { useState, useCallback, useEffect, useMemo, useRef } from "react"

import { isEditableElement } from "./utils"

import styles from "../TaigaPage.module.scss"
import { useIsMobile } from "../shared/CreateSessionModal"
import NodeRow, {
	type SharedNode,
	type NodeDragOverEvent,
	buildTree,
	hasChildren,
} from "../shared/NodeRow"
import { appConfirm } from "../shared/dialog"

/**
 * Build a depth-first ordered flat list of focusable node IDs.
 * Respects isCollapsed: collapsed nodes hide their children from the list.
 */
const buildFocusableList = (
	allNodes: SharedNode[],
	parentId: string | null
): string[] => {
	const children = buildTree(allNodes, parentId)
	const result: string[] = []
	for (const node of children) {
		result.push(node.id)
		if (!node.isCollapsed) {
			result.push(...buildFocusableList(allNodes, node.id))
		}
	}
	return result
}

/** Focus request from parent to enter this tree. */
export type NodeTreeFocusRequest = {
	mentionId: string
	direction: "top" | "bottom"
	seq: number
} | null

type MentionNodeTreeProps = {
	nodes: SharedNode[]
	mentionId: string
	isReadOnly: boolean
	onUpdateNode: (id: string, name: string, isCollapsed?: boolean) => void
	onArchiveNode: (id: string) => void
	// node 追加はセッション作成モーダル (node 新規作成モード) を開く。
	// prompt 入力・作成処理は親 (TaigaMentionsSection) 側で行う。
	onAddChildNode: (milestoneId: string | null, parentNodeId: string) => void
	onAddRootNode: () => void
	// embed session 引き継ぎ: 「+ node を追加」の隣のボタンから URL 入力モーダルを開く。
	onHandOverEmbedSession?: () => void
	// Seq3: node ツリー操作ハンドラ (TaigaSheetDetailPage と同等)
	onPriorityUp: (nodeId: string) => void
	onPriorityDown: (nodeId: string) => void
	onCreateSession: (nodeId: string, nodeName: string) => void
	onCompleteSession: (
		nodeId: string,
		nodeName: string,
		hasSession: boolean
	) => void
	onPendSession: (nodeId: string) => void
	onOpenSlack: (nodeId: string, threadTs: string, channelId: string) => void
	onCopyBranch: (nodeId: string) => void
	onForkSession?: (nodeId: string, nodeName: string) => void
	onOpenMemo: (node: SharedNode) => void
	onMoveNode: (
		nodeId: string,
		targetParentNodeId: string | null,
		afterNodeId: string | null
	) => void
	onReorderNodes: (parentNodeId: string | null, nodeIds: string[]) => void
	// Focus navigation: escape callbacks when at tree boundary
	onFocusEscapeUp?: () => void
	onFocusEscapeDown?: () => void
	// Focus request from parent (e.g. when pressing ↓ from mention)
	focusRequest?: NodeTreeFocusRequest
	// 親メンションが現在フォーカスされているか。
	// フォーカスが他メンションへ移ったら、このツリーの node フォーカスも解除する。
	isParentMentionFocused: boolean
	// このツリーで node がフォーカスされたか（child → parent への通知）。
	// 親はこの間、メンション用ショートカット（E / 3 / 4 / j / k 等）を処理しない。
	onFocusedNodeChange?: (nodeId: string | null) => void
	// cc セッション選択時の階層名サフィックス (例: "p1")。
	// 指定すると選択名が "{sort位置パス}-{suffix}:{node.name}" になる
	sessionNameSuffix?: string
}

const MentionNodeTree: FC<MentionNodeTreeProps> = ({
	nodes,
	mentionId,
	isReadOnly,
	onUpdateNode,
	onArchiveNode,
	onAddChildNode,
	onAddRootNode,
	onHandOverEmbedSession,
	onPriorityUp,
	onPriorityDown,
	onCreateSession,
	onCompleteSession,
	onPendSession,
	onOpenSlack,
	onCopyBranch,
	onForkSession,
	onOpenMemo,
	onMoveNode,
	onReorderNodes,
	onFocusEscapeUp,
	onFocusEscapeDown,
	focusRequest,
	isParentMentionFocused,
	onFocusedNodeChange,
	sessionNameSuffix,
}) => {
	const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null)
	const isMobile = useIsMobile()
	const [editingNodeId, setEditingNodeId] = useState<string | null>(null)

	// Flat list of visible node IDs for j/k / arrow key navigation
	const focusableList = useMemo(() => buildFocusableList(nodes, null), [nodes])

	// Drag & Drop state
	const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null)
	const [dragOverInfo, setDragOverInfo] = useState<{
		nodeId: string
		mode: "sibling" | "child"
	} | null>(null)

	// Handle focus request from parent (enter tree from mention)
	const prevFocusSeqRef = useRef(0)
	useEffect(() => {
		if (!focusRequest) return
		if (focusRequest.mentionId !== mentionId) return
		if (focusRequest.seq === prevFocusSeqRef.current) return
		prevFocusSeqRef.current = focusRequest.seq
		if (focusableList.length === 0) return
		if (focusRequest.direction === "top") {
			setFocusedNodeId(focusableList[0])
		} else {
			setFocusedNodeId(focusableList[focusableList.length - 1])
		}
	}, [focusRequest, mentionId, focusableList])

	// フォーカスが他メンションへ移ったら、このツリーの node フォーカスを解除する。
	// node フォーカスが残っていると keydown を stopImmediatePropagation で奪い続け、
	// 別メンションをクリックしてもキーボードフォーカスがそちらへ移らないため。
	useEffect(() => {
		if (!isParentMentionFocused) {
			setFocusedNodeId(null)
		}
	}, [isParentMentionFocused])

	// node フォーカスの有無を親へ通知する（child → parent）。
	// 親（TaigaMentionsSection）はこのツリーに node フォーカスがある間、
	// メンション用ショートカット（E でコメント編集 / 3・4 で priority / j・k で移動 等）を
	// 処理せず、フォーカス中のノード側にショートカットを委ねる。
	// ref 経由で呼ぶことで、親が渡す callback の identity が毎 render 変わっても
	// effect が無駄に再実行されないようにする。
	const onFocusedNodeChangeRef = useRef(onFocusedNodeChange)
	onFocusedNodeChangeRef.current = onFocusedNodeChange
	const prevReportedFocusRef = useRef<string | null>(null)
	useEffect(() => {
		if (prevReportedFocusRef.current === focusedNodeId) return
		prevReportedFocusRef.current = focusedNodeId
		onFocusedNodeChangeRef.current?.(focusedNodeId)
	}, [focusedNodeId])
	// ツリー折りたたみ等でアンマウントされたら node フォーカス解除を親へ通知する。
	useEffect(() => {
		return () => onFocusedNodeChangeRef.current?.(null)
	}, [])

	// done したセッションは表示しない（斜線ではなく非表示）
	const rootNodes = buildTree(nodes, null).filter((node) => !node.isDone)

	const handleRename = useCallback(
		(id: string, name: string) => {
			onUpdateNode(id, name)
		},
		[onUpdateNode]
	)

	const handleToggleCollapse = useCallback(
		(id: string, isCollapsed: boolean) => {
			const node = nodes.find((n) => n.id === id)
			if (!node) return
			onUpdateNode(id, node.name, isCollapsed)
		},
		[nodes, onUpdateNode]
	)

	const handleAddChild = useCallback(
		(milestoneId: string | null, parentId: string) => {
			if (isReadOnly) return
			onAddChildNode(milestoneId, parentId)
		},
		[isReadOnly, onAddChildNode]
	)

	// Wrap onCompleteSession for NodeRow: NodeRow calls (nodeId) but parent expects (nodeId, nodeName, hasSession).
	// MentionNodeTree has access to the full node data so it can look up and pass the extra info.
	const handleCompleteSession = useCallback(
		(nodeId: string) => {
			const node = nodes.find((n) => n.id === nodeId)
			if (!node) return
			onCompleteSession(nodeId, node.name, !!node.session)
		},
		[nodes, onCompleteSession]
	)

	// Drag & Drop handlers (same logic as TaigaSheetDetailPage)
	const handleDragStart = useCallback((nodeId: string) => {
		setDraggedNodeId(nodeId)
	}, [])

	const handleDragOver = useCallback(
		(e: NodeDragOverEvent, nodeId: string) => {
			e.preventDefault()
			if (draggedNodeId && draggedNodeId !== nodeId) {
				const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
				const relX = e.clientX - rect.left
				const mode = relX > rect.width * 0.5 ? "child" : "sibling"
				setDragOverInfo({
					nodeId,
					mode: mode as "sibling" | "child",
				})
			}
		},
		[draggedNodeId]
	)

	const handleDragLeave = useCallback(() => {
		setDragOverInfo(null)
	}, [])

	const handleDrop = useCallback(
		(targetNodeId: string) => {
			if (!draggedNodeId || draggedNodeId === targetNodeId) return

			const dropMode = dragOverInfo?.mode ?? "sibling"
			const draggedNode = nodes.find((n) => n.id === draggedNodeId)
			const targetNode = nodes.find((n) => n.id === targetNodeId)
			if (!draggedNode || !targetNode) return

			// Prevent moving into own descendants
			const isDescendant = (parentId: string, checkId: string): boolean => {
				const children = nodes.filter((n) => n.parentNodeId === parentId)
				for (const child of children) {
					if (child.id === checkId) return true
					if (isDescendant(child.id, checkId)) return true
				}
				return false
			}

			if (dropMode === "child") {
				if (isDescendant(draggedNodeId, targetNode.id)) {
					setDraggedNodeId(null)
					setDragOverInfo(null)
					return
				}
				onMoveNode(draggedNodeId, targetNode.id, null)
			} else {
				// Sibling drop
				const sameParent = draggedNode.parentNodeId === targetNode.parentNodeId

				if (sameParent) {
					// Same parent: reorder within siblings
					const siblings = buildTree(nodes, draggedNode.parentNodeId)
					const currentOrder = siblings.map((n) => n.id)
					const fromIdx = currentOrder.indexOf(draggedNodeId)
					const toIdx = currentOrder.indexOf(targetNodeId)
					if (fromIdx === -1 || toIdx === -1) return

					const newOrder = [...currentOrder]
					newOrder.splice(fromIdx, 1)
					newOrder.splice(toIdx, 0, draggedNodeId)

					onReorderNodes(draggedNode.parentNodeId, newOrder)
				} else {
					// Cross-parent move
					if (
						targetNode.parentNodeId &&
						isDescendant(draggedNodeId, targetNode.parentNodeId)
					) {
						setDraggedNodeId(null)
						setDragOverInfo(null)
						return
					}
					onMoveNode(draggedNodeId, targetNode.parentNodeId, targetNode.id)
				}
			}

			setDraggedNodeId(null)
			setDragOverInfo(null)
		},
		[draggedNodeId, dragOverInfo, nodes, onMoveNode, onReorderNodes]
	)

	const handleDragEnd = useCallback(() => {
		setDraggedNodeId(null)
		setDragOverInfo(null)
	}, [])

	// Keyboard shortcuts (same as TaigaSheetDetailPage node shortcuts)
	// Only captures events when a node is focused (focusedNodeId != null).
	// Uses stopImmediatePropagation to prevent parent handlers from also firing.
	useEffect(() => {
		if (isReadOnly) return
		if (editingNodeId) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (isEditableElement(e.target)) return

			// Navigation keys: only handle when a node is already focused
			if (
				e.key === "j" ||
				e.key === "ArrowDown" ||
				e.key === "k" ||
				e.key === "ArrowUp"
			) {
				// Don't capture navigation when no node is focused;
				// let parent (TaigaMentionsSection) handle mention navigation.
				if (focusedNodeId === null) return
				if (focusableList.length === 0) return

				e.preventDefault()
				e.stopImmediatePropagation()

				const currentIdx = focusableList.indexOf(focusedNodeId)
				if (currentIdx === -1) return

				if (e.key === "j" || e.key === "ArrowDown") {
					if (currentIdx >= focusableList.length - 1) {
						// At bottom boundary: escape down to next mention
						setFocusedNodeId(null)
						onFocusEscapeDown?.()
					} else {
						setFocusedNodeId(focusableList[currentIdx + 1])
					}
				} else {
					if (currentIdx <= 0) {
						// At top boundary: escape up to parent mention
						setFocusedNodeId(null)
						onFocusEscapeUp?.()
					} else {
						setFocusedNodeId(focusableList[currentIdx - 1])
					}
				}
				return
			}

			// Action keys: only handle when a node is focused
			if (!focusedNodeId) return

			const node = nodes.find((n) => n.id === focusedNodeId)
			if (!node) return

			const isLeaf = !hasChildren(nodes, node.id)

			let handled = false

			switch (e.key) {
				case "s":
				case "S":
					if (isLeaf && !node.session) {
						onCreateSession(node.id, node.name)
						handled = true
					}
					break
				case "q":
				case "Q":
					if (!node.isDone) {
						onCompleteSession(node.id, node.name, !!node.session)
						handled = true
					}
					break
				case "a":
				case "A":
					if (!node.session) {
						if (e.shiftKey) {
							// 兄弟ノード追加: root ノードの兄弟は mention 直下の root ノード
							if (node.parentNodeId) {
								onAddChildNode(node.milestoneId, node.parentNodeId)
							} else {
								onAddRootNode()
							}
						} else {
							onAddChildNode(node.milestoneId, node.id)
						}
						handled = true
					}
					break
				case "3":
					onPriorityUp(node.id)
					handled = true
					break
				case "4":
					onPriorityDown(node.id)
					handled = true
					break
				case "e":
				case "E":
					setEditingNodeId(node.id)
					handled = true
					break
				case "6":
					void appConfirm("このノードをアーカイブしますか？").then((ok) => {
						if (ok) onArchiveNode(node.id)
					})
					handled = true
					break
				case "7":
					if (
						node.session &&
						!node.isDone &&
						node.session.sessionStatus !== "PENDING"
					) {
						onPendSession(node.id)
						handled = true
					}
					break
				case "1":
					if (node.session?.threadTs) {
						onOpenSlack(node.id, node.session.threadTs, node.session.channelId)
						handled = true
					}
					break
				case "2":
					if (node.session) {
						onCopyBranch(node.id)
						handled = true
					}
					break
				case "m":
				case "M":
					if (node.session && !node.isDone) {
						onOpenMemo(node)
						handled = true
					}
					break
			}

			if (handled) {
				e.preventDefault()
				e.stopImmediatePropagation()
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [
		isReadOnly,
		editingNodeId,
		focusedNodeId,
		focusableList,
		nodes,
		onCreateSession,
		onCompleteSession,
		onAddChildNode,
		onAddRootNode,
		onPriorityUp,
		onPriorityDown,
		onArchiveNode,
		onPendSession,
		onOpenSlack,
		onCopyBranch,
		onOpenMemo,
		onFocusEscapeUp,
		onFocusEscapeDown,
	])

	return (
		<div className={styles.mentionNodesTree}>
			{rootNodes.map((node, i) => (
				<NodeRow
					key={node.id}
					node={node}
					allNodes={nodes}
					depth={0}
					focusedNodeId={focusedNodeId}
					isMobile={isMobile}
					hideDone={true}
					onFocus={setFocusedNodeId}
					onRename={handleRename}
					onAddChild={handleAddChild}
					onDelete={
						isReadOnly
							? () => {
									/* noop: read-only mode */
								}
							: onArchiveNode
					}
					onPriorityUp={onPriorityUp}
					onPriorityDown={onPriorityDown}
					onCreateSession={onCreateSession}
					onCompleteSession={handleCompleteSession}
					onPendSession={onPendSession}
					onOpenSlack={onOpenSlack}
					onCopyBranch={onCopyBranch}
					onForkSession={onForkSession}
					onOpenMemo={onOpenMemo}
					editingNodeId={editingNodeId}
					setEditingNodeId={setEditingNodeId}
					draggedNodeId={draggedNodeId}
					dragOverInfo={dragOverInfo}
					onDragStart={handleDragStart}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onDragEnd={handleDragEnd}
					onToggleCollapse={handleToggleCollapse}
					sessionNamePath={String(i + 1)}
					sessionNameSuffix={sessionNameSuffix}
				/>
			))}
			{!isReadOnly && (
				<div className={styles.mentionNodeActionRow}>
					<button
						type="button"
						className={styles.mentionAddNodeButton}
						onClick={(event) => {
							event.stopPropagation()
							onAddRootNode()
						}}
					>
						+ node を追加
					</button>
					{onHandOverEmbedSession && (
						<button
							type="button"
							className={styles.mentionAddNodeButton}
							onClick={(event) => {
								event.stopPropagation()
								onHandOverEmbedSession()
							}}
						>
							embed 引き継ぎ
						</button>
					)}
				</div>
			)}
		</div>
	)
}

export default MentionNodeTree
